
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import VideoModal from './VideoModal';

export default function Features() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isVisible = useScrollAnimation(sectionRef, { threshold: 0.1 });
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const navigate = useNavigate();

  const modulesByCategory = [
    {
      category: 'Gestión Core',
      icon: 'ri-dashboard-line',
      color: 'bg-cyan-500',
      modules: [
        {
          id: 'centro-comando',
          title: 'Centro de Comando & Mapa Interactivo',
          badge: 'Core',
          badgeColor: 'bg-cyan-500',
          description: 'Dashboard en tiempo real con métricas clave + Mapa interactivo con plano de planta por colores. Visión completa del estado del hospital en un solo lugar.',
          bullets: ['Ocupación en vivo', 'Mapa visual por colores', 'Altas previstas', 'Estados en tiempo real'],
          image: 'https://img.youtube.com/vi/fTYGDCG7iqY/maxresdefault.jpg',
          size: 'large',
          demoVideo: 'https://www.youtube.com/embed/fTYGDCG7iqY?autoplay=1'
        },
        {
          id: 'configuracion',
          title: '100% Adaptable a tu Centro',
          badge: 'Config',
          badgeColor: 'bg-purple-500',
          description: 'Crea plantas, añade camas, edita estructura en minutos. Sistema totalmente configurable que se adapta a cualquier hospital.',
          bullets: ['Crear/editar plantas', 'Añadir/modificar camas', 'Estructura flexible', 'Configuración visual'],
          image: 'https://img.youtube.com/vi/EiRCAbv95fc/maxresdefault.jpg',
          size: 'large',
          demoVideo: 'https://www.youtube.com/embed/EiRCAbv95fc?autoplay=1'
        }
      ]
    },
    {
      category: 'Inteligencia Artificial',
      icon: 'ri-brain-line',
      color: 'bg-purple-500',
      modules: [
        {
          id: 'prediccion-ia',
          title: 'Predicción IA para TODOS los Pacientes',
          badge: 'IA',
          badgeColor: 'bg-purple-500',
          description: 'Probabilidad de alta + horizonte estimado para cada paciente. Criterios visibles y editables. El equipo valida y decide.',
          bullets: ['Probabilidad de alta', 'Horizonte temporal', 'Criterios visibles', 'Base editable'],
          image: 'https://img.youtube.com/vi/lrz__lm_Vxg/maxresdefault.jpg',
          size: 'medium',
          demoVideo: 'https://www.youtube.com/embed/lrz__lm_Vxg?autoplay=1'
        },
        {
          id: 'asignacion-inteligente',
          title: 'Asignación Inteligente de Cama',
          badge: 'IA',
          badgeColor: 'bg-purple-500',
          description: 'IA propone la mejor cama según patología, requisitos de aislamiento y compatibilidad. Scoring transparente.',
          bullets: ['Análisis de patología', 'Requisitos aislamiento', 'Compatibilidad', 'Scoring inteligente'],
          image: 'https://img.youtube.com/vi/wHXW7HeSjXA/maxresdefault.jpg',
          size: 'medium',
          demoVideo: 'https://www.youtube.com/embed/wHXW7HeSjXA?autoplay=1'
        },
        {
          id: 'comunicacion-familiar',
          title: 'Comunicación Familiar Inteligente',
          badge: 'IA',
          badgeColor: 'bg-purple-500',
          description: 'Gestión de contactos + plantillas editables. IA propone borradores según situación del paciente. Tú revisas y decides qué enviar.',
          bullets: ['Contactos organizados', 'Plantillas editables', 'IA propone borradores', 'Validación humana'],
          image: 'https://img.youtube.com/vi/GhdmJEINI10/maxresdefault.jpg',
          size: 'medium',
          demoVideo: 'https://www.youtube.com/embed/GhdmJEINI10?autoplay=1'
        }
      ]
    },
    {
      category: 'Coordinación Operativa',
      icon: 'ri-message-line',
      color: 'bg-green-500',
      modules: [
        {
          id: 'mensajeria-operativa',
          title: 'Mensajería Operativa Sin Llamadas',
          badge: 'Flujo',
          badgeColor: 'bg-green-500',
          description: 'Coordinación trazable con limpieza y mantenimiento. Notificación → Confirmación → Actualización automática. Sin llamadas.',
          bullets: ['Notificaciones automáticas', 'Confirmación trazable', 'Sin llamadas', 'Estados en vivo'],
          image: 'https://img.youtube.com/vi/jfqxb0vznyA/maxresdefault.jpg',
          size: 'medium',
          demoVideo: 'https://www.youtube.com/embed/jfqxb0vznyA?autoplay=1'
        }
      ]
    },
    {
      category: 'Análisis y Datos',
      icon: 'ri-bar-chart-line',
      color: 'bg-cyan-500',
      modules: [
        {
          id: 'analitica',
          title: 'Analítica Hospitalaria',
          badge: 'Datos',
          badgeColor: 'bg-cyan-500',
          description: 'Métricas clave: ocupación histórica, rotación de camas, estancia media, tiempos operativos y tendencias.',
          bullets: ['Ocupación histórica', 'Rotación de camas', 'Estancia media', 'Tiempos operativos'],
          image: 'https://img.youtube.com/vi/1bU2RNoiCnM/maxresdefault.jpg',
          size: 'medium',
          demoVideo: 'https://www.youtube.com/embed/1bU2RNoiCnM?autoplay=1'
        },
        {
          id: 'base-conocimiento',
          title: 'Base de Conocimiento Médico',
          badge: 'Config',
          badgeColor: 'bg-purple-500',
          description: 'Estancias por patología editables. Define rangos y añade notas clínicas para afinar las predicciones de la IA.',
          bullets: ['Estancias por patología', 'Rangos editables', 'Notas clínicas', 'Mejora continua'],
          image: 'https://img.youtube.com/vi/9akVMoiUuKQ/maxresdefault.jpg',
          size: 'medium',
          demoVideo: 'https://www.youtube.com/embed/9akVMoiUuKQ?autoplay=1'
        }
      ]
    }
  ];

  const scrollToModule = (moduleId: string) => {
    const element = document.getElementById(moduleId);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - 100; // Offset optimizado
      
      // Scroll ultra fluido con requestAnimationFrame para máxima suavidad
      requestAnimationFrame(() => {
        setTimeout(() => {
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }, 50);
      });
    }
  };

  return (
    <section ref={sectionRef} id="features-section" className="py-16 sm:py-24 bg-gray-50 relative overflow-hidden">
      {/* Decorative dots */}
      <div className="absolute inset-0 dots-pattern"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header con animación */}
        <div className={`text-center mb-10 sm:mb-16 space-y-3 sm:space-y-4 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="inline-block glass px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold border border-primary-400/30">
            <span className="gradient-text">Módulos Completos</span>
          </div>
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            Módulos que <span className="gradient-text text-glow">transforman</span><br className="hidden sm:block" />
            <span className="sm:hidden"> </span>la operación
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            Cada módulo está diseñado para resolver un problema específico del flujo hospitalario
          </p>
        </div>

        {/* Modules by Category */}
        {modulesByCategory.map((categoryGroup, catIndex) => (
          <div key={catIndex} className="mb-12 sm:mb-16">
            {/* Category Header */}
            <div className={`flex items-center gap-3 mb-6 sm:mb-8 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`} style={{ transitionDelay: `${200 + catIndex * 100}ms` }}>
              <div className={`w-10 h-10 sm:w-12 sm:h-12 ${categoryGroup.color} rounded-xl flex items-center justify-center`}>
                <i className={`${categoryGroup.icon} text-white text-xl sm:text-2xl`}></i>
              </div>
              <h3 className="text-xl sm:text-3xl font-bold text-gray-900">{categoryGroup.category}</h3>
              <div className="flex-1 h-px bg-gradient-to-r from-gray-300 to-transparent"></div>
            </div>

            {/* Modules Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
              {categoryGroup.modules.map((module, index) => (
                <div
                  key={module.id}
                  id={module.id}
                  onClick={() => scrollToModule(module.id)}
                  className={`group card p-5 sm:p-8 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 cursor-pointer ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
                  }`}
                  style={{ transitionDelay: `${400 + catIndex * 200 + index * 100}ms` }}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
                    <h3 className="text-lg sm:text-2xl font-bold text-gray-900 flex-1 group-hover:text-primary-500 transition-colors duration-300">{module.title}</h3>
                    <span className={`px-2 sm:px-3 py-0.5 sm:py-1 ${module.badgeColor} text-white rounded-full text-xs font-bold whitespace-nowrap`}>
                      {module.badge}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">{module.description}</p>

                  {/* Bullets */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-6">
                    {module.bullets.map((bullet, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 sm:gap-2">
                        <div className="w-4 h-4 sm:w-5 sm:h-5 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-white text-xs"></i>
                        </div>
                        <span className="text-xs sm:text-sm text-gray-700 font-medium">{bullet}</span>
                      </div>
                    ))}
                  </div>

                  {/* Image con overlay */}
                  <div className={`relative rounded-lg sm:rounded-xl overflow-hidden bg-gray-100 group/image ${
                    module.image.includes('youtube.com') || module.image.includes('img.youtube.com') ? 'aspect-video' : ''
                  }`}>
                    <img
                      src={module.image}
                      alt={module.title}
                      className={`w-full transition-transform duration-700 group-hover:scale-105 ${
                        module.image.includes('youtube.com') || module.image.includes('img.youtube.com')
                          ? 'h-full object-cover'
                          : 'h-40 sm:h-64 object-cover object-top'
                      }`}
                    />
                    
                    {/* Overlay con botón de demo - siempre visible en móvil, hover en desktop */}
                    {module.demoVideo.startsWith('http') && (
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/95 via-gray-900/70 to-transparent opacity-100 sm:opacity-0 sm:group-hover/image:opacity-100 transition-all duration-300 flex flex-col items-center justify-end pb-4 sm:pb-8 gap-2 sm:gap-4">
                        <p className="text-white text-xs sm:text-sm font-semibold">
                          <i className="ri-play-circle-line mr-2"></i>
                          Demostración en video
                        </p>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setVideoUrl(module.demoVideo);
                          }}
                          className="btn-primary text-xs sm:text-base flex items-center gap-2"
                        >
                          <i className="ri-play-circle-line"></i>
                          <span>Ver demo</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* CTA Final */}
        <div className={`mt-10 sm:mt-16 text-center space-y-4 sm:space-y-6 transition-all duration-1000 delay-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <p className="text-gray-600 text-sm sm:text-lg">
            ¿Quieres ver todos los módulos en acción?
          </p>
          <button
            onClick={() => {
              // Si estamos en la página de inicio, hacer scroll
              const demoSection = document.getElementById('demo-section');
              if (demoSection) {
                const elementPosition = demoSection.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - 30; // Offset reducido para más arriba
                
                // Scroll profesional ultra fluido
                requestAnimationFrame(() => {
                  setTimeout(() => {
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }, 50);
                });
              } else {
                // Navegar a inicio con state indicando que queremos ir a demo
                navigate('/', { state: { scrollTo: 'demo-section' } });
              }
            }}
            className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base"
          >
            <span>Probar la demo completa</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>

      {/* Video Modal */}
      {videoUrl && (
        <VideoModal 
          videoUrl={videoUrl} 
          onClose={() => setVideoUrl(null)} 
        />
      )}
    </section>
  );
}
